/**
 * The OrobasHipercube, drawn by the animated .lottie that ships with the app.
 *
 * Each animation is the mark for one subscription plan: default, silver, gold.
 * The file is the same asset the macOS app loads, so the site and the app never
 * disagree about what the cube looks like.
 */

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import type { DotLottie } from '@lottiefiles/dotlottie-web';

export type HipercubePlan = 'default' | 'silver' | 'gold';

const SRC: Record<HipercubePlan, string> = {
  default: '/animations/OrobasHipercubeDefaultAnim.lottie',
  silver: '/animations/OrobasHipercubeSilverAnim.lottie',
  gold: '/animations/OrobasHipercubeGoldAnim.lottie',
};

export const HipercubeLottie: React.FC<{
  plan?: HipercubePlan;
  plans?: readonly HipercubePlan[];
  cycleIntervalMs?: number;
  cycleOnComplete?: boolean;
  size?: number | 'full';
  className?: string;
  title?: string | null;
}> = ({
  plan = 'default',
  plans,
  cycleIntervalMs = 1200,
  cycleOnComplete = false,
  size = 120,
  className,
  title,
}) => {
  const sequence = plans && plans.length > 0 ? plans : [plan];
  const [index, setIndex] = useState(0);
  const [prevIndex, setPrevIndex] = useState<number | null>(null);
  const [dotLottie, setDotLottie] = useState<DotLottie | null>(null);
  const isTransitioning = useRef(false);
  const transitionTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const currentPlan = sequence[index] ?? plan;
  const prevPlan = prevIndex !== null ? sequence[prevIndex] : null;

  const handleTransitionEnd = useCallback(() => {
    setPrevIndex(null);
    isTransitioning.current = false;
  }, []);

  // Timer-based cycling (used where a fixed cadence is wanted).
  useEffect(() => {
    if (cycleOnComplete || sequence.length <= 1) return;
    const timer = window.setInterval(() => {
      setIndex((current) => {
        const next = (current + 1) % sequence.length;
        if (!isTransitioning.current) {
          isTransitioning.current = true;
          setPrevIndex(current);
          if (transitionTimeout.current) clearTimeout(transitionTimeout.current);
          transitionTimeout.current = window.setTimeout(handleTransitionEnd, 400);
        }
        return next;
      });
    }, cycleIntervalMs);
    return () => {
      window.clearInterval(timer);
      if (transitionTimeout.current) clearTimeout(transitionTimeout.current);
    };
  }, [cycleOnComplete, sequence.length, cycleIntervalMs, handleTransitionEnd]);

  // Completion-based cycling: advance only after the current animation finishes.
  useEffect(() => {
    if (!cycleOnComplete || sequence.length <= 1 || dotLottie === null) return;
    const onComplete = () => {
      setIndex((current) => {
        const next = (current + 1) % sequence.length;
        if (!isTransitioning.current) {
          isTransitioning.current = true;
          setPrevIndex(current);
          if (transitionTimeout.current) clearTimeout(transitionTimeout.current);
          transitionTimeout.current = window.setTimeout(handleTransitionEnd, 400);
        }
        return next;
      });
    };
    dotLottie.addEventListener('complete', onComplete);
    return () => {
      dotLottie.removeEventListener('complete', onComplete);
      if (transitionTimeout.current) clearTimeout(transitionTimeout.current);
    };
  }, [cycleOnComplete, sequence.length, dotLottie, handleTransitionEnd]);

  const containerStyle = {
    width: size === 'full' ? '100%' : size,
    height: size === 'full' ? '100%' : size,
    aspectRatio: '1 / 1',
  };

  return (
    <div
      className={className}
      style={containerStyle}
      role={title == null ? 'presentation' : 'img'}
      aria-label={title ?? undefined}
      aria-hidden={title == null ? true : undefined}
    >
      {/* Previous animation (cross-fade out) */}
      {prevPlan && (
        <div
          key={`prev-${prevPlan}`}
          className="absolute inset-0 flex items-center justify-center transition-opacity duration-400"
          style={{ opacity: 0 }}
        >
          <DotLottieReact
            src={SRC[prevPlan]}
            loop={false}
            autoplay
            style={{ width: '100%', height: '100%', aspectRatio: '1 / 1' }}
          />
        </div>
      )}

      {/* Current animation (cross-fade in) */}
      <div className="absolute inset-0 flex items-center justify-center transition-opacity duration-400">
        <DotLottieReact
          src={SRC[currentPlan]}
          loop={false}
          autoplay
          dotLottieRefCallback={setDotLottie}
          style={{ width: '100%', height: '100%', aspectRatio: '1 / 1' }}
        />
      </div>

      {/* Metallic gold reflection overlay for gold plan */}
      {currentPlan === 'gold' && (
        <div
          className="absolute inset-0 pointer-events-none hipercube-gold-reflection"
          style={{ borderRadius: 'inherit' }}
          aria-hidden="true"
        />
      )}
    </div>
  );
};

export function planForProduct(productId: string): HipercubePlan {
  if (productId.includes('gold') || productId.includes('suite')) return 'gold';
  if (productId.includes('silver')) return 'silver';
  return 'default';
}